'use client'

import React from 'react'
import Image from 'next/image'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { getDevicePlatform } from '@utils/segmentProperties'
import { CONTACT_US_EVENTS } from '@configs/contactus.config'
import styles from './callback.module.scss'

interface CallbackSectionProps {
  heading: string
  subheading: string
  ctaPrimary: string
  ctaSecondary: string
  CallbackMedia: {
    data: {
      id: number
      attributes: {
        url: string
        width: number
        height: number
        alternativeText: string | null
        formats?: {
          large?: { url: string; width: number; height: number }
          medium?: { url: string; width: number; height: number }
          small?: { url: string; width: number; height: number }
        }
      }
    }
  }
  isMobile?: boolean
  triggerCopilot?: (message: string) => void
  uid?: string
}

const CallbackSection: React.FC<CallbackSectionProps> = ({
  heading,
  subheading,
  ctaPrimary,
  ctaSecondary,
  CallbackMedia,
  triggerCopilot,
  uid,
}) => {
  // flag to enable/disable the callback section
  if (!heading) return

  const {
    url: imageUrl,
    alternativeText,
    width: imageWidth = 658,
    height: imageHeight = 359,
  } = CallbackMedia?.data?.attributes || {}
  const imageAlt = alternativeText || 'ACKO Expert'

  const handleCtaClick = async (msg: string = '') => {
    if (triggerCopilot) {
      triggerCopilot(msg)
    }
    const event = msg.length ? CONTACT_US_EVENTS.REQUEST_CALLBACK_CLICKED : CONTACT_US_EVENTS.CHAT_NOW_SALES_CLICKED

    await trackEvent(event.eventName, {
      ...event.eventProps,
      platform: getDevicePlatform(),
    })
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <section className={styles.callbackSection}>
      <div className={styles.container}>
        {imageUrl && (
          <div className={styles.imageWrapper}>
            <Image
              src={imageUrl}
              alt={imageAlt}
              width={imageWidth}
              height={imageHeight}
              className={styles.image}
              priority={false}
            />
          </div>
        )}

        <div className={styles.contentWrapper}>
          <div className={styles.textContent}>
            <h2 className={styles.heading}>{heading}</h2>
            <p className={styles.subheading}>{subheading}</p>
          </div>

          <div className={styles.ctaGroup}>
            <button
              className={styles.ctaPrimary}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={() => handleCtaClick(ctaPrimary)}
              type="button">
              {ctaPrimary}
            </button>
            <button
              className={styles.ctaSecondary}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={() => handleCtaClick()}
              type="button">
              {ctaSecondary}
            </button>
          </div>
        </div>
      </div>
      {uid && (
        <div className={styles.uidSection}>
          <span className={styles.uidText}>UID: {uid}</span>
        </div>
      )}
    </section>
  )
}

export default CallbackSection
